import React, { useState, useMemo, useCallback, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainer";
import { useQuery, useQueryClient } from "react-query";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Spin } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import "../../Styles/style.css";
import Flag from 'react-world-flags';  // Importation de react-world-flags
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';  // Importation de useTranslation
import { parsePhoneNumberFromString } from 'libphonenumber-js';  // Importation de libphonenumber-js
import { deleteHistory, getHistory } from "../services/HistoryApi";
// import DetailHistorique from "../component/DetailHistorique";

const HistoriqueVehicleChauffeur = () => {
  const { t } = useTranslation();  // Hook pour récupérer les traductions
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  document.title = `Historiques | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedHistorique, setSelectedHistorique] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetFullName, setDeleteTargetFullName] = useState("");

  const { isLoading, isError, error, data: historiques } = useQuery("historiques", getHistory);

  const toggleAddEditModal = useCallback((historique = null) => {
    setSelectedHistorique(historique);
    setIsAddEditModalOpen(prev => !prev);
    if (isDetailModalOpen) setIsDetailModalOpen(false);
  }, [isDetailModalOpen]);

  const handleEdit = (historique) => {
    toggleAddEditModal(historique);
  };

  const toggleDetailModal = useCallback((historique = null) => {
    setSelectedHistorique(historique);
    setIsDetailModalOpen(prev => !prev);
    if (isAddEditModalOpen) setIsAddEditModalOpen(false);
  }, [isAddEditModalOpen]);

  
  const handleDetail = (historique) => {
    toggleDetailModal(historique);
  };

  const handleDelete = async (historiqueId) => {
    try {
      await deleteHistory(historiqueId);
      queryClient.invalidateQueries("historiques");
      toast.success("Le chauffeur a été supprimé avec succès 🎉");
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast.error(`😔 Erreur lors de la suppression du chauffeur : ${error?.message || error}`);
    } finally {
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (historiqueId) => {
    const historiqueToDelete = historiques.find(historique => historique.id === historiqueId);
    if (historiqueToDelete) {
      setDeleteTargetId(historiqueId);
      setDeleteTargetFullName(historiqueToDelete.firstName + " " + driverToDelete.lastName);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };
  const columns = useMemo(() => [
    {
      Header: "Avatar",
      accessor: "Avatar",
      Cell: ({ row }) => (
        <div className="avatar"><span>{row.original.user.name.charAt(0).toUpperCase()}</span></div>
      ),
      filterable: false,
    },
  
    { Header: () => <div style={{ textAlign: "center" }}>{t("drivers.lastName")}</div>, accessor: "name", Cell: ({ row }) => (
      <div className="text-center">{row.original.user.name || t("drivers.name")}</div>),
      filterable: false },
   
      {
        Header: () => <div style={{ textAlign: "center" }}>{t("historique.role")}</div>,
        accessor: "role",
        Cell: ({ row }) => (
          <div className="text-center">{row.original.user.role?.name || t("historique.noRole")}</div>),
        filterable: false
      },
    {
      Header: () => <div style={{ textAlign: "center" }}>{t("historique.email")}</div>,
      accessor: "email",
      Cell: ({ row }) => (
        <div className="text-center">{row.original.user?.email || t("historique.noEmail")}</div>),
      filterable: false
    },
    {
      Header: () => <div style={{ textAlign: "center" }}>{t("historique.adress")}</div>,
      accessor: "adresse",
      Cell: ({ row }) => (
        <div className="text-center">{row.original.user?.address|| t("historique.noAdress")}</div>),
      filterable: false
    },
    /** { 
  
      Header: ()=><div style={{textAlign:"center"}}> {t("historique.cni")}</div>,
      accessor: "cni",
      Cell: ({ row }) => (
      <div className="text-center">{row.original.cni || "---"}</div>),
      filterable: false 
    },
    
    { 
  
      Header: ()=><div style={{textAlign:"center"}}> {t("historique.permis")}</div>,
      accessor: "permis",
      Cell: ({ row }) => (
      <div className="text-center">{row.original.pemis || "---"}</div>),
      filterable: false 
    },*/
    {
      Header: () => <div style={{ textAlign: "center" }}>{t("historique.phone")}</div>,
      accessor: "phone",
      Cell: ({ row }) => {
        const phoneNumber = parsePhoneNumberFromString(row.original.user.phone); // Utilisation de libphonenumber-js
    const countryCode = phoneNumber ? phoneNumber.country : 'SN'; // Par défaut 'SN' pour
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Flag code={countryCode}style={{ width: 20, height: 15 }} />
            </div>
            <span>{row.original.user.phone}</span>
          </div>
        );
      },
      filterable: false
    },
    {
      Header: () => <div style={{ textAlign: "end" }}>{t("historique.actions")}</div>,
      accessor: "action",
      headerStyle: { textAlign: "text-center" },
      Cell: ({ row }) => (
        <div className=" actions-cell">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-detail">{t("drivers.viewDetails")}</Tooltip>}
          >
            <EyeOutlined style={{ color: '#1F5357', fontSize: '15px' }} onClick={() => handleDetail(row.original)} />
          </OverlayTrigger>
        </div>
      ),
      filterable: false,
    },
  ], [handleDetail, openDeleteConfirmModal]);

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">
        <strong>{t("historique.listTitle")}</strong>
      </div>
      
    </div>
  );

  const tabs = [t("drivers.all"), t("drivers.assigned"), t("drivers.unassigned"), t("drivers.archived")];
  const [activeTab, setActiveTab] = useState(t("drivers.all"));

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const filteredHistoriques = useMemo(() => {
    if (!historiques) return error?.message || [];
    switch (activeTab) {
      case t("historiques.assigned"):
        return historiques.filter(v => v.status === 'Assigne');
      case t("drivers.unassigned"):
        return historiques.filter(v => v.status === 'Non assigne');
      case t("historiques.archived"):
        return historiques.filter(v => v.status === 'Archive');
      default:
        return historiques;
    }
  }, [historiques, activeTab]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page={t("historique.historique")} history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainerMin
                    columns={columns}
                    data={filteredHistoriques}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={5}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder={t("drivers.searchPlaceholder")}
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      {t("drivers.loading")} <Spin />
                    </div>
                  )}
                  {isError && (
                    <div className="text-center">
                      {t("drivers.error")}: {error?.message || error}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
         
           {/* <DetailHistorique
            modal={isDetailModalOpen}
            setModal={setIsDetailModalOpen}
            toggle={toggleDetailModal}
            selectedHistorique={selectedHistorique}
          /> */}
          <Modal
            title={t("drivers.deleteConfirmationTitle")}
            open={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={[
              <Button key="cancel" onClick={closeDeleteConfirmModal} className="bordered-btn-delete">
                {t("drivers.no")}
              </Button>,
              <Button key="delete" type="danger" onClick={() => handleDelete(deleteTargetId)} className="bordered-btn">
                {t("drivers.yes")}
              </Button>,
            ]}
            width={400}
            style={{ top: 200 }}
          >
            <p>{t("drivers.deleteConfirmationMessage")}{deleteTargetFullName} ?</p>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default HistoriqueVehicleChauffeur;
